import type { ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';

type FieldProps = {
  label: string;
  hint?: string;
  htmlFor?: string;
  error?: string | null;
  required?: boolean;
  children: ReactNode;
};

/**
 * Generic form-field wrapper matching the Alhudah app voice:
 * uppercase label, calm hint, ink-500 error tone.
 */
export function QariFormField({
  label,
  hint,
  htmlFor,
  error,
  required,
  children,
}: FieldProps) {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={htmlFor}
        className="block text-xs uppercase tracking-[0.18em] text-ink-500 dark:text-ink-400"
      >
        {label}
        {required && <span className="text-accent-600 ml-1">*</span>}
      </label>
      {children}
      {hint && !error && (
        <p className="text-xs text-ink-500 dark:text-ink-400 leading-relaxed">
          {hint}
        </p>
      )}
      {error && (
        <p
          className="text-xs text-rose-600 dark:text-rose-400 leading-relaxed"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
}

type TextInputProps = {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: 'text' | 'email' | 'tel' | 'number';
  maxLength?: number;
  disabled?: boolean;
  className?: string;
};

export function QariTextInput({
  id,
  value,
  onChange,
  placeholder,
  type = 'text',
  maxLength,
  disabled,
  className,
}: TextInputProps) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      maxLength={maxLength}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        'w-full h-10 px-3 rounded-lg border bg-transparent text-sm',
        'border-ink-200/80 dark:border-ink-800/80',
        'text-ink-900 dark:text-ink-50',
        'placeholder:text-ink-400 dark:placeholder:text-ink-600',
        'focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent',
        'disabled:opacity-60 disabled:cursor-not-allowed',
        className,
      )}
    />
  );
}

type TextAreaProps = {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  disabled?: boolean;
};

export function QariTextArea({
  id,
  value,
  onChange,
  placeholder,
  rows = 4,
  maxLength,
  disabled,
}: TextAreaProps) {
  return (
    <textarea
      id={id}
      rows={rows}
      maxLength={maxLength}
      disabled={disabled}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        'w-full px-3 py-2 rounded-lg border bg-transparent text-sm',
        'border-ink-200/80 dark:border-ink-800/80',
        'text-ink-900 dark:text-ink-50 leading-relaxed',
        'placeholder:text-ink-400 dark:placeholder:text-ink-600',
        'focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent',
        'disabled:opacity-60 disabled:cursor-not-allowed resize-y',
      )}
    />
  );
}
